import React, {useState, useEffect, useContext} from 'react';
import {FormControlLabel, ListItem, Radio, RadioGroup, Typography, useTheme} from '@mui/material';
import {Stack} from '@mui/system';
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { simpTextAddContact, simpTextAddUnsub, simpTextRemoveUnsub } from '@lib/api';
import { paletteMains, scrollToAlert, tenDigit } from '@lib/utils';
import UserContext from '@lib/contexts/userContext';
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from "@lib/contexts/pageContext";

const ActivityItem = ({ activity, userActivities }) => {
  const queryClient = useQueryClient();

  const {
    setPageErrorMessage,
  } = useContext(PageContext);

  const { userState: { userRecord }, setUserRecord } = useContext(UserContext);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const isEnrollable = (activity.id !== '1'); // Users cannot manually enroll/un-enroll from signing up on the app (activity 1)

  const theme = useTheme();
  const palette = paletteMains(theme);

  const getSimpTextPhone = () => tenDigit(userRecord.phone_number);

  useEffect(() => {
    if (activity.id === '1') {
      setIsEnrolled(true);
    } else if (activity.id === '2') {
      setIsEnrolled(!!userRecord?.allow_enews);
    } else if (activity.id === '3') {
      setIsEnrolled(!!userRecord?.allow_text_program);
    } else if (userActivities && userActivities.length > 0) {
      const foundActivity = userActivities.find((userActivity) => activity.id === userActivity.activityID);
      setIsEnrolled(!!foundActivity);
    }
  }, [activity.id, userActivities, userRecord?.allow_enews, userRecord?.allow_text_program]);

  const simpTextAdd = async () => {
    try {
      await simpTextRemoveUnsub({ phone: getSimpTextPhone() });
      await simpTextAddContact({
        group: userRecord.county,
        phone: getSimpTextPhone(),
        firstName: userRecord.first_name,
        lastName: userRecord.last_name,
        email: userRecord.email,
      });
      return true;
    } catch (err) {
      console.log('SimpText remUnsub/add error', err);
      return false;
    }
  };

  const assignActivityMutation = useMutation({
    mutationFn: async (activity_id) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.ACTIVITIES}/assign`,
      data: {
        activity_id,
      }
    }),
    onSuccess: async (user) => {
      console.log('Success assigning', user);
      setPageErrorMessage('');
      setUserRecord(user);
      queryClient.setQueryData(['current_user', 'activities'], user.activities);
    },
    onError: async (err) => {
      setPageErrorMessage('Error saving activity status');
      console.log('[SessionDetails][undo] error:', err);
      scrollToAlert();
    },
  });

  const removeActivityMutation = useMutation({
    mutationFn: async (activity_id) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.ACTIVITIES}/remove`,
      data: {
        activity_id,
      }
    }),
    onSuccess: async (user) => {
      console.log('Success removing', user);
      setPageErrorMessage('');
      setUserRecord(user);
      queryClient.setQueryData(['current_user', 'activities'], user.activities);
    },
    onError: async (err) => {
      setPageErrorMessage('Error saving activity status');
      console.log('[SessionDetails][undo] error:', err);
      scrollToAlert();
    },
  });

  const handleRadioOnChange = async () => {
    try {
      if (isEnrolled) {

        // Un-enroll user
        setIsEnrolled(false);

        // If this is the text program, attempt to unsubscribe first via SimpleTexting
        if (activity.id === '3') {
          try {
            await simpTextAddUnsub({ phone: getSimpTextPhone() });
          } catch (err) {
            console.log('SimpText unsub error', err);
            setPageErrorMessage('Error unsubscribing you from the text message program!');
            setIsEnrolled(true);
            return; // Prevents updating the user activities/score
          }
        }
        removeActivityMutation.mutate(activity.id);

      } else {

        // Enroll user
        setIsEnrolled(true);

        // If this is the text program, attempt to subscribe first via SimpleTexting
        if (activity.id === '3') {
          try {
            await simpTextAdd();
          } catch (err) {
            console.log('SimpText sub error', err);
            setPageErrorMessage('Error subscribing you to the text message program!');
            setIsEnrolled(false);
            return; // Prevents updating the user activities/score
          }
        }
        assignActivityMutation.mutate(activity.id);

      }
    } catch (error) {
      console.log('Error: ', error);
      setIsEnrolled(isEnrolled);
    }
  };

  return (
    <ListItem disablePadding>
      <Stack>
        <Typography variant="h3" component="h3" fontWeight="bold">
          {activity.title.toUpperCase()}
        </Typography>
        <Typography variant="h7" component="div" sx={{ color: palette.green }}>
          {activity.points} Points
        </Typography>
        <Typography variant="body1" component="div" sx={{ wordWrap: 'break-word' }}>
          {activity.description}
        </Typography>
        {isEnrollable &&
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name={activity.activity_code}
            value={isEnrolled ? 1 : 0}
            onChange={handleRadioOnChange}
            row
            sx={{
              justifyContent: 'space-around',
              maxWidth: '350px',
            }}
          >
            <FormControlLabel value="0" control={<Radio />} label="Not Enrolled" />
            <FormControlLabel value="1" control={<Radio />} label="Enrolled" />
          </RadioGroup>
        }
      </Stack>
    </ListItem>
  );
};

export default ActivityItem;
